<template>
  <div class="experience-container">
    <!-- Hero Header Section -->
    <v-card class="hero-header mb-6" elevation="0">
      <div class="hero-content">
        <div class="hero-icon-wrapper">
          <v-icon size="48" color="white">mdi-account-school</v-icon>
        </div>
        <div class="hero-text">
          <h1 class="hero-title">การพัฒนาตนเองและวิชาชีพ</h1>
          <p class="hero-subtitle">ประวัติการอบรม สัมมนา และการพัฒนาทักษะวิชาชีพ</p>
        </div>
        <v-spacer></v-spacer>
        <v-btn class="add-btn" large depressed @click="personnel_experienceAdd()">
          <v-icon left>mdi-plus</v-icon>
          เพิ่มรายการ
        </v-btn>
      </div>
    </v-card>

    <!-- Statistics Cards -->
    <v-row class="stats-row mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-1" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-certificate</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ personnel_experiences.length }}</span>
            <span class="stat-label">รายการทั้งหมด</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-2" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-account-cog</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ countByType('self') }}</span>
            <span class="stat-label">พัฒนาตนเอง</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-3" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-briefcase-account</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ countByType('professional') }}</span>
            <span class="stat-label">พัฒนาวิชาชีพ</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-4" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-clock-outline</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ totalHours.toLocaleString() }}</span>
            <span class="stat-label">ชั่วโมงรวม</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Data Table Card -->
    <v-card class="main-table-card" elevation="0">
      <v-card-title class="table-header">
        <div class="header-left">
          <v-icon class="mr-3" color="#dc2626">mdi-format-list-bulleted</v-icon>
          <span class="header-title">รายการการพัฒนาตนเอง / วิชาชีพ</span>
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="ค้นหารายการ..."
          single-line
          hide-details
          dense
          outlined
          class="search-field"
          style="max-width: 350px"
        ></v-text-field>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table
        :loading="loading"
        :headers="headers"
        :items="personnel_experiences"
        :search="search"
        :items-per-page="10"
        class="modern-table"
        loading-text="กำลังโหลดข้อมูล..."
        no-data-text="ไม่พบข้อมูลการพัฒนาตนเอง/วิชาชีพ"
        no-results-text="ไม่พบผลลัพธ์ที่ค้นหา"
      >
        <template v-slot:item="{ item, index }">
          <tr class="table-row">
            <td class="text-center">
              <v-avatar size="32" color="#dc2626" class="white--text font-weight-bold">
                {{ index + 1 }}
              </v-avatar>
            </td>
            <td class="text-center">
              <v-chip small color="#fef2f2" text-color="#dc2626" class="year-chip">
                <v-icon left small>mdi-calendar</v-icon>
                {{ item.pexperienceyear_s }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-chip
                small
                :color="getTypeColor(item.pexperienceType)"
                dark
                class="type-chip"
              >
                <v-icon left small>{{ getTypeIcon(item.pexperienceType) }}</v-icon>
                {{ getTypeText(item.pexperienceType) }}
              </v-chip>
            </td>
            <td>
              <div class="course-cell">
                <v-icon small color="#f97316" class="mr-2">mdi-book-open-variant</v-icon>
                <span class="course-text">{{ item.course_name }}</span>
              </div>
            </td>
            <td>
              <div class="location-cell">
                <v-icon small color="#0ea5e9" class="mr-1">mdi-map-marker</v-icon>
                {{ item.course_location }}
              </div>
            </td>
            <td>
              <div class="agency-cell">
                <v-icon small color="#8b5cf6" class="mr-1">mdi-domain</v-icon>
                {{ item.agency_name }}
              </div>
            </td>
            <td class="text-center">
              <v-chip small color="#f0fdf4" text-color="#16a34a" class="date-chip">
                {{ item.date_time_begin | moment("add", "543 years") | moment("D MMM YY") }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-chip small color="#fef3c7" text-color="#d97706" class="date-chip">
                {{ item.date_time_end | moment("add", "543 years") | moment("D MMM YY") }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-chip small color="#e0e7ff" text-color="#4f46e5" class="hours-chip">
                <v-icon left small>mdi-clock</v-icon>
                {{ item.number_hours }} ชม.
              </v-chip>
            </td>
            <td class="text-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="action-btn edit-btn mr-1"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="personnel_experienceEdit(item.id_pe)"
                  >
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <span>แก้ไข</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="action-btn delete-btn"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="personnel_experienceDelete(item.id_pe)"
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
                <span>ลบ</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add Experience Dialog -->
    <v-dialog
      v-model="addpersonnel_experiencedialog"
      persistent
      max-width="750px"
      content-class="modern-dialog"
    >
      <v-card class="dialog-card">
        <v-card-title class="dialog-header add-header">
          <v-icon class="mr-3" color="white" size="28">mdi-plus-circle-outline</v-icon>
          <span>เพิ่มการพัฒนาตนเอง / วิชาชีพ</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="addpersonnel_experiencedialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="addpersonnel_experienceform" lazy-validation>
            <!-- Section: ข้อมูลทั่วไป -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#dc2626" class="mr-2">mdi-information-outline</v-icon>
                ข้อมูลทั่วไป
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="addpersonnel_experience.pexperienceyear_s"
                    label="ปี พ.ศ."
                    placeholder="เช่น 2567"
                    type="number"
                    :rules="[
                      v => !!v || 'กรุณาระบุปี',
                      v => (v && String(v).length === 4) || 'ปีต้องเป็นตัวเลข 4 หลัก'
                    ]"
                    outlined
                    dense
                    prepend-inner-icon="mdi-calendar"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="addpersonnel_experience.pexperienceType"
                    :items="pexperienceTypeselect"
                    item-text="text"
                    item-value="value"
                    label="ประเภทการพัฒนา"
                    :rules="[v => !!v || 'กรุณาเลือกประเภท']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-tag-outline"
                    class="modern-input"
                  >
                    <template v-slot:item="{ item }">
                      <v-icon class="mr-2" :color="getTypeColor(item.value)" small>
                        {{ getTypeIcon(item.value) }}
                      </v-icon>
                      {{ item.text }}
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" v-if="addpersonnel_experience.pexperienceType">
                  <v-alert
                    dense
                    text
                    :color="addpersonnel_experience.pexperienceType === 'self' ? 'info' : 'success'"
                    class="type-description"
                  >
                    <v-icon slot="prepend" :color="addpersonnel_experience.pexperienceType === 'self' ? 'info' : 'success'">
                      {{ addpersonnel_experience.pexperienceType === 'self' ? 'mdi-account-cog' : 'mdi-briefcase-account' }}
                    </v-icon>
                    <span v-if="addpersonnel_experience.pexperienceType === 'self'">
                      <strong>การพัฒนาตนเอง:</strong> การอบรมสัมมนา ศึกษาดูงานเพิ่มพูนความรู้ และการศึกษาต่อ
                    </span>
                    <span v-else>
                      <strong>การพัฒนาวิชาชีพ:</strong> การแต่งตั้งเป็นคณะกรรมการ วิทยากร นวัตกรรม สื่อการสอน งานวิจัย
                    </span>
                  </v-alert>
                </v-col>
              </v-row>
            </div>

            <!-- Section: รายละเอียดโครงการ -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#f97316" class="mr-2">mdi-book-open-page-variant</v-icon>
                รายละเอียดโครงการ / หลักสูตร
              </div>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="addpersonnel_experience.course_name"
                    label="ชื่อโครงการ / หลักสูตร"
                    placeholder="ระบุชื่อโครงการหรือหลักสูตรที่เข้าร่วม"
                    :rules="[v => !!v || 'กรุณาระบุชื่อโครงการ']"
                    outlined
                    rows="2"
                    auto-grow
                    prepend-inner-icon="mdi-text-box-outline"
                    class="modern-input"
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="addpersonnel_experience.course_location"
                    label="สถานที่จัดอบรม"
                    placeholder="เช่น โรงแรมเซ็นทารา กรุงเทพฯ"
                    :rules="[v => !!v || 'กรุณาระบุสถานที่']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-map-marker"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="addpersonnel_experience.agency_name"
                    label="หน่วยงานที่จัด"
                    placeholder="เช่น สอศ., มหาวิทยาลัย..."
                    :rules="[v => !!v || 'กรุณาระบุหน่วยงาน']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-domain"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <!-- Section: ระยะเวลา -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#16a34a" class="mr-2">mdi-calendar-range</v-icon>
                ระยะเวลาการอบรม
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-menu
                    ref="menu"
                    v-model="menu"
                    :close-on-content-click="false"
                    :return-value.sync="date"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="addpersonnel_experience.date_time_begin"
                        label="วันที่เริ่มต้น"
                        prepend-inner-icon="mdi-calendar-start"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        class="modern-input"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="addpersonnel_experience.date_time_begin"
                      no-title
                      scrollable
                      locale="th-TH"
                      color="#dc2626"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="grey" @click="menu = false">ยกเลิก</v-btn>
                      <v-btn text color="#dc2626" @click="$refs.menu.save(date)">ตกลง</v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="4">
                  <v-menu
                    ref="menu2"
                    v-model="menu2"
                    :close-on-content-click="false"
                    :return-value.sync="date"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="addpersonnel_experience.date_time_end"
                        label="วันที่สิ้นสุด"
                        prepend-inner-icon="mdi-calendar-end"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        class="modern-input"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="addpersonnel_experience.date_time_end"
                      no-title
                      scrollable
                      locale="th-TH"
                      color="#dc2626"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="grey" @click="menu2 = false">ยกเลิก</v-btn>
                      <v-btn text color="#dc2626" @click="$refs.menu2.save(date)">ตกลง</v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="addpersonnel_experience.number_hours"
                    label="จำนวนชั่วโมง"
                    placeholder="เช่น 12"
                    type="number"
                    :rules="[v => !!v || 'กรุณาระบุจำนวนชั่วโมง']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-clock-outline"
                    suffix="ชั่วโมง"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text large @click="addpersonnel_experiencedialog = false" class="cancel-btn">
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            large
            depressed
            color="#dc2626"
            dark
            @click="addpersonnel_experienceSubmit()"
            class="save-btn"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            บันทึกข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Experience Dialog -->
    <v-dialog
      v-model="editpersonnel_experiencedialog"
      persistent
      max-width="750px"
      content-class="modern-dialog"
    >
      <v-card class="dialog-card">
        <v-card-title class="dialog-header edit-header">
          <v-icon class="mr-3" color="white" size="28">mdi-pencil-outline</v-icon>
          <span>แก้ไขการพัฒนาตนเอง / วิชาชีพ</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="editpersonnel_experiencedialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="editpersonnel_experienceform" lazy-validation>
            <!-- Section: ข้อมูลทั่วไป -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#f97316" class="mr-2">mdi-information-outline</v-icon>
                ข้อมูลทั่วไป
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editpersonnel_experience.pexperienceyear_s"
                    label="ปี พ.ศ."
                    type="number"
                    :rules="[
                      v => !!v || 'กรุณาระบุปี',
                      v => (v && String(v).length === 4) || 'ปีต้องเป็นตัวเลข 4 หลัก'
                    ]"
                    outlined
                    dense
                    prepend-inner-icon="mdi-calendar"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="editpersonnel_experience.pexperienceType"
                    :items="pexperienceTypeselect"
                    item-text="text"
                    item-value="value"
                    label="ประเภทการพัฒนา"
                    :rules="[v => !!v || 'กรุณาเลือกประเภท']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-tag-outline"
                    class="modern-input"
                  ></v-select>
                </v-col>
              </v-row>
            </div>

            <!-- Section: รายละเอียดโครงการ -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#0ea5e9" class="mr-2">mdi-book-open-page-variant</v-icon>
                รายละเอียดโครงการ / หลักสูตร
              </div>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="editpersonnel_experience.course_name"
                    label="ชื่อโครงการ / หลักสูตร"
                    :rules="[v => !!v || 'กรุณาระบุชื่อโครงการ']"
                    outlined
                    rows="2"
                    auto-grow
                    prepend-inner-icon="mdi-text-box-outline"
                    class="modern-input"
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editpersonnel_experience.course_location"
                    label="สถานที่จัดอบรม"
                    :rules="[v => !!v || 'กรุณาระบุสถานที่']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-map-marker"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editpersonnel_experience.agency_name"
                    label="หน่วยงานที่จัด"
                    :rules="[v => !!v || 'กรุณาระบุหน่วยงาน']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-domain"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <!-- Section: ระยะเวลา -->
            <div class="form-section">
              <div class="section-title">
                <v-icon color="#16a34a" class="mr-2">mdi-calendar-range</v-icon>
                ระยะเวลาการอบรม
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-menu
                    ref="menu3"
                    v-model="menu3"
                    :close-on-content-click="false"
                    :return-value.sync="date"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="editpersonnel_experience.date_time_begin"
                        label="วันที่เริ่มต้น"
                        prepend-inner-icon="mdi-calendar-start"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        class="modern-input"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="editpersonnel_experience.date_time_begin"
                      no-title
                      scrollable
                      locale="th-TH"
                      color="#f97316"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="grey" @click="menu3 = false">ยกเลิก</v-btn>
                      <v-btn text color="#f97316" @click="$refs.menu3.save(date)">ตกลง</v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="4">
                  <v-menu
                    ref="menu4"
                    v-model="menu4"
                    :close-on-content-click="false"
                    :return-value.sync="date"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="editpersonnel_experience.date_time_end"
                        label="วันที่สิ้นสุด"
                        prepend-inner-icon="mdi-calendar-end"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        class="modern-input"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="editpersonnel_experience.date_time_end"
                      no-title
                      scrollable
                      locale="th-TH"
                      color="#f97316"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="grey" @click="menu4 = false">ยกเลิก</v-btn>
                      <v-btn text color="#f97316" @click="$refs.menu4.save(date)">ตกลง</v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="editpersonnel_experience.number_hours"
                    label="จำนวนชั่วโมง"
                    type="number"
                    :rules="[v => !!v || 'กรุณาระบุจำนวนชั่วโมง']"
                    outlined
                    dense
                    prepend-inner-icon="mdi-clock-outline"
                    suffix="ชั่วโมง"
                    class="modern-input"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text large @click="editpersonnel_experiencedialog = false" class="cancel-btn">
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            large
            depressed
            color="#f97316"
            dark
            @click="editpersonnel_experienceSubmit()"
            class="save-btn"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            บันทึกการแก้ไข
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="deletepersonnel_experiencedialog"
      persistent
      max-width="500px"
      content-class="modern-dialog"
    >
      <v-card class="dialog-card delete-dialog">
        <v-card-title class="dialog-header delete-header">
          <v-icon class="mr-3" color="white" size="28">mdi-delete-alert-outline</v-icon>
          <span>ยืนยันการลบข้อมูล</span>
        </v-card-title>

        <v-card-text class="delete-content text-center">
          <v-form ref="deletepersonnel_experienceform" lazy-validation>
            <div class="delete-icon-wrapper">
              <v-icon size="72" color="#dc2626">mdi-alert-circle-outline</v-icon>
            </div>
            <div class="delete-message">คุณต้องการลบรายการนี้หรือไม่?</div>

            <v-card class="delete-info-card mt-4" outlined>
              <v-card-text class="pa-4">
                <div class="delete-info-item">
                  <v-icon small color="#f97316" class="mr-2">mdi-book-open-variant</v-icon>
                  <span class="info-text">{{ editpersonnel_experience.course_name }}</span>
                </div>
              </v-card-text>
            </v-card>

            <div class="delete-warning mt-4">
              <v-icon small color="#f97316" class="mr-1">mdi-alert</v-icon>
              การดำเนินการนี้ไม่สามารถย้อนกลับได้
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text large @click="deletepersonnel_experiencedialog = false" class="cancel-btn">
            <v-icon left>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            large
            depressed
            color="#dc2626"
            dark
            @click="deletepersonnel_experienceubmit()"
            class="delete-confirm-btn"
          >
            <v-icon left>mdi-delete-outline</v-icon>
            ลบข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
      class="modern-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-3" color="white">{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'PersonnelExperience',

  data() {
    return {
      ApiKey: 'HRvec2021',
      loading: true,
      search: '',
      addpersonnel_experiencedialog: false,
      editpersonnel_experiencedialog: false,
      deletepersonnel_experiencedialog: false,
      snackbar: {
        show: false,
        color: '',
        timeout: 4000,
        icon: '',
        text: ''
      },
      headers: [
        { text: '#', align: 'center', value: 'index', width: '60px', sortable: false },
        { text: 'ปี', align: 'center', value: 'pexperienceyear_s', width: '100px' },
        { text: 'ประเภท', align: 'center', value: 'pexperienceType', width: '140px' },
        { text: 'โครงการ / หลักสูตร', align: 'left', value: 'course_name' },
        { text: 'สถานที่', align: 'left', value: 'course_location', width: '150px' },
        { text: 'หน่วยงาน', align: 'left', value: 'agency_name', width: '150px' },
        { text: 'เริ่ม', align: 'center', value: 'date_time_begin', width: '100px' },
        { text: 'สิ้นสุด', align: 'center', value: 'date_time_end', width: '100px' },
        { text: 'ชั่วโมง', align: 'center', value: 'number_hours', width: '90px' },
        { text: 'จัดการ', align: 'center', value: 'actions', sortable: false, width: '100px' }
      ],
      personnel_experiences: [],
      addpersonnel_experience: {},
      editpersonnel_experience: {},
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      menu: false,
      menu2: false,
      menu3: false,
      menu4: false,
      pexperienceTypeselect: [
        { text: 'การพัฒนาตนเอง', value: 'self' },
        { text: 'การพัฒนาวิชาชีพ', value: 'professional' }
      ]
    }
  },

  computed: {
    totalHours() {
      return this.personnel_experiences.reduce((sum, item) => {
        return sum + (parseInt(item.number_hours) || 0)
      }, 0)
    }
  },

  async mounted() {
    await this.personnel_experiencesQueryAll()
  },

  methods: {
    countByType(type) {
      return this.personnel_experiences.filter(item => item.pexperienceType === type).length
    },

    getTypeColor(type) {
      return type === 'self' ? '#0ea5e9' : '#16a34a'
    },

    getTypeIcon(type) {
      return type === 'self' ? 'mdi-account-cog' : 'mdi-briefcase-account'
    },

    getTypeText(type) {
      return type === 'self' ? 'พัฒนาตนเอง' : 'พัฒนาวิชาชีพ'
    },

    async personnel_experiencesQueryAll() {
      this.loading = true
      let userSession = JSON.parse(localStorage.getItem('user')) || 0
      let result = await this.$http
        .post('personnel_experience.php', {
          ApiKey: this.ApiKey,
          pexperienceid_card: userSession.id_card
        })
        .finally(() => (this.loading = false))
      this.personnel_experiences = result.data || []
    },

    personnel_experienceAdd() {
      this.addpersonnel_experience = {}
      this.addpersonnel_experiencedialog = true
    },

    async addpersonnel_experienceSubmit() {
      if (this.$refs.addpersonnel_experienceform.validate()) {
        let userSession = JSON.parse(localStorage.getItem('user')) || 0
        this.addpersonnel_experience.ApiKey = this.ApiKey
        this.addpersonnel_experience.pexperienceid_card = userSession.id_card

        let result = await this.$http.post(
          'personnel_experience.insert.php',
          this.addpersonnel_experience
        )

        if (result.data.status === true) {
          Swal.fire({
            icon: 'success',
            title: 'บันทึกสำเร็จ',
            text: 'เพิ่มรายการพัฒนาเรียบร้อยแล้ว',
            showConfirmButton: false,
            timer: 1500
          })
          this.personnel_experiencesQueryAll()
        } else {
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถบันทึกข้อมูลได้',
            showConfirmButton: true
          })
        }
        this.addpersonnel_experiencedialog = false
      }
    },

    async personnel_experienceEdit(id_pe) {
      let result = await this.$http.post('personnel_experience.php', {
        ApiKey: this.ApiKey,
        id_pe: id_pe
      })
      this.editpersonnel_experience = result.data
      this.editpersonnel_experiencedialog = true
    },

    async editpersonnel_experienceSubmit() {
      if (this.$refs.editpersonnel_experienceform.validate()) {
        this.editpersonnel_experience.ApiKey = this.ApiKey

        let result = await this.$http.post(
          'personnel_experience.update.php',
          this.editpersonnel_experience
        )

        if (result.data.status === true) {
          Swal.fire({
            icon: 'success',
            title: 'แก้ไขสำเร็จ',
            text: 'อัปเดตรายการพัฒนาเรียบร้อยแล้ว',
            showConfirmButton: false,
            timer: 1500
          })
          this.personnel_experiencesQueryAll()
        } else {
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถแก้ไขข้อมูลได้',
            showConfirmButton: true
          })
        }
        this.editpersonnel_experiencedialog = false
      }
    },

    async personnel_experienceDelete(id_pe) {
      let result = await this.$http.post('personnel_experience.php', {
        ApiKey: this.ApiKey,
        id_pe: id_pe
      })
      this.editpersonnel_experience = result.data
      this.deletepersonnel_experiencedialog = true
    },

    async deletepersonnel_experienceubmit() {
      this.editpersonnel_experience.ApiKey = this.ApiKey

      let result = await this.$http.post(
        'personnel_experience.delete.php',
        this.editpersonnel_experience
      )

      if (result.data.status === true) {
        Swal.fire({
          icon: 'success',
          title: 'ลบสำเร็จ',
          text: 'ลบรายการพัฒนาเรียบร้อยแล้ว',
          showConfirmButton: false,
          timer: 1500
        })
        this.personnel_experiencesQueryAll()
      } else {
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถลบข้อมูลได้',
          showConfirmButton: true
        })
      }
      this.deletepersonnel_experiencedialog = false
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Prompt:wght@300;400;500;600;700&display=swap');

.experience-container {
  font-family: 'Prompt', sans-serif;
  padding: 24px;
  background: linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%);
  min-height: 100vh;
}

/* Hero Header */
.hero-header {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 10px 40px rgba(220, 38, 38, 0.3);
}

.hero-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.06'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.hero-content {
  display: flex;
  align-items: center;
  padding: 32px;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
  gap: 16px;
}

.hero-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.hero-text {
  color: white;
}

.hero-title {
  font-size: 26px;
  font-weight: 600;
  margin: 0 0 6px 0;
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
}

.add-btn {
  background: white !important;
  color: #dc2626 !important;
  font-weight: 500;
  border-radius: 12px;
  padding: 0 28px !important;
  text-transform: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Statistics Cards */
.stats-row {
  margin-top: -16px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-card-1 .stat-icon { background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); }
.stat-card-2 .stat-icon { background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%); }
.stat-card-3 .stat-icon { background: linear-gradient(135deg, #16a34a 0%, #15803d 100%); }
.stat-card-4 .stat-icon { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.stat-label {
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
}

/* Main Table Card */
.main-table-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.table-header {
  padding: 20px 24px;
  background: #fafafa;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.search-field {
  background: white;
}

.search-field >>> .v-input__slot {
  border-radius: 12px !important;
}

/* Data Table */
.modern-table {
  font-family: 'Prompt', sans-serif;
}

.modern-table >>> thead th {
  background: #fef2f2 !important;
  color: #991b1b !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 2px solid #fecaca !important;
  padding: 14px 8px !important;
}

.table-row {
  transition: all 0.2s ease;
}

.table-row:hover {
  background: #fffbfb !important;
}

.table-row td {
  padding: 14px 8px !important;
  border-bottom: 1px solid #f3f4f6 !important;
  font-size: 13px;
  color: #374151;
}

.course-cell {
  display: flex;
  align-items: flex-start;
  max-width: 250px;
}

.course-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.location-cell,
.agency-cell {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.type-chip {
  font-weight: 500;
  font-size: 11px !important;
}

.date-chip,
.hours-chip {
  font-size: 11px !important;
}

/* Action Buttons */
.action-btn {
  width: 32px !important;
  height: 32px !important;
  transition: all 0.2s ease;
}

.edit-btn {
  background: #fff7ed !important;
  color: #f97316 !important;
}

.edit-btn:hover {
  background: #ffedd5 !important;
  transform: scale(1.1);
}

.delete-btn {
  background: #fef2f2 !important;
  color: #dc2626 !important;
}

.delete-btn:hover {
  background: #fecaca !important;
  transform: scale(1.1);
}

/* Dialog Styles */
.modern-dialog {
  border-radius: 20px !important;
}

.dialog-card {
  border-radius: 20px !important;
  overflow: hidden;
}

.dialog-header {
  padding: 20px 24px;
  font-size: 18px;
  font-weight: 600;
}

.add-header {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
}

.edit-header {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  color: white;
}

.delete-header {
  background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
  color: white;
}

.dialog-content {
  padding: 24px !important;
  max-height: 65vh;
  overflow-y: auto;
}

.form-section {
  background: #fafafa;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid #f3f4f6;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.modern-input >>> .v-input__slot {
  background: white !important;
  border-radius: 10px !important;
}

.type-description {
  border-radius: 10px !important;
  font-size: 13px;
}

.dialog-actions {
  padding: 16px 24px;
  background: #fafafa;
}

.cancel-btn {
  color: #6b7280 !important;
  font-weight: 500;
  text-transform: none;
}

.save-btn {
  border-radius: 10px;
  font-weight: 500;
  text-transform: none;
  padding: 0 24px !important;
}

/* Delete Dialog */
.delete-content {
  padding: 32px 24px !important;
}

.delete-icon-wrapper {
  margin-bottom: 16px;
}

.delete-message {
  font-size: 18px;
  font-weight: 500;
  color: #1f2937;
}

.delete-info-card {
  background: #fafafa;
  border-radius: 12px !important;
  border-color: #e5e7eb !important;
  text-align: left;
}

.delete-info-item {
  display: flex;
  align-items: flex-start;
}

.info-text {
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
}

.delete-warning {
  font-size: 13px;
  color: #f97316;
}

.delete-confirm-btn {
  border-radius: 10px;
  font-weight: 500;
  text-transform: none;
}

/* Responsive */
@media (max-width: 960px) {
  .hero-content {
    padding: 24px;
  }

  .hero-icon-wrapper {
    width: 64px;
    height: 64px;
  }

  .hero-title {
    font-size: 20px;
  }

  .add-btn {
    width: 100%;
  }

  .course-cell {
    max-width: 150px;
  }
}

@media (max-width: 600px) {
  .experience-container {
    padding: 16px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-number {
    font-size: 24px;
  }
}

/* Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card {
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
}

.stat-card:nth-child(1) { animation-delay: 0.1s; }
.stat-card:nth-child(2) { animation-delay: 0.2s; }
.stat-card:nth-child(3) { animation-delay: 0.3s; }
.stat-card:nth-child(4) { animation-delay: 0.4s; }

/* Custom scrollbar */
.dialog-content::-webkit-scrollbar {
  width: 6px;
}

.dialog-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}
</style>